/* eslint-disable react/prop-types */
import * as React from 'react';

import { useLockFn, useUpdateEffect } from 'ahooks';
import { Badge, Button, Dropdown, Flex, Modal, notification } from 'antd';

import {
  AntdRouterLink,
  CheckCircleOutlinedIcon,
  CloseCircleOutlinedIcon,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  MoreOutlinedIcon,
  PrimitiveTable,
} from 'components';
import { deleteDiscountCode } from 'services';
import type { DiscountCodeSummary } from 'types';
import {
  adjustPageNumber,
  dateTimeFormat,
  generateTableDataSource,
  localizeDiscountCodeEnable,
  localizeDiscountCodeType,
  numberFormat,
  PATHNAME,
} from 'utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    DISCOUNT_CODES: { UPDATE_URL: DISCOUNT_CODES_UPDATE_URL },
  },
} = PATHNAME;

type DiscountCodesTableProps = {
  loading: boolean;
  total: number;
  discountCodes: DiscountCodeSummary[];
  pageSize: number;
  pageNumber: number;
  handlePageSize: (pageSize: number) => void;
  handlePageNumber: (pageNumber: number) => void;
  handleRefreshData: () => void;
};

const DiscountCodesTable = React.memo<DiscountCodesTableProps>(
  ({
    loading,
    total,
    discountCodes,
    pageSize,
    pageNumber,
    handlePageSize,
    handlePageNumber,
    handleRefreshData,
  }) => {
    const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();

    useUpdateEffect(() => {
      adjustPageNumber(total, pageSize, pageNumber, handlePageNumber);
    }, [total, pageSize, pageNumber]);

    const handleDeleteDiscountCode = useLockFn(
      async ({ id }: DiscountCodeSummary) => {
        deleteDiscountCode({ id })
          .then(() => {
            handleRefreshData();
            notification.success({
              message: 'کد تخفیف مورد نظر با موفقیت حذف شد.',
            });
          })
          .catch(message => {
            notification.error({
              message,
            });
          });
      },
    );

    const discountCodesDataSource = generateTableDataSource(
      discountCodes,
      'id',
    );

    return (
      <PrimitiveTable
        scroll={{ x: 900, y: 700 }}
        columns={[
          {
            title: 'شناسه',
            dataIndex: 'code',
            key: 'code',
            width: '20%',
            ellipsis: true,
            render: (_, { id, code }) => (
              <AntdRouterLink
                ellipsis
                to={`${DISCOUNT_CODES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
              >
                {code}
              </AntdRouterLink>
            ),
          },
          {
            title: 'مقدار',
            dataIndex: 'amount',
            key: 'amount',
            ellipsis: true,
            render: (_, { type, amount }) => {
              const { label } = localizeDiscountCodeType(type);
              return (
                <span>
                  {numberFormat(amount)} {label}
                </span>
              );
            },
          },
          {
            title: 'وضعیت',
            dataIndex: 'enable',
            key: 'enable',
            width: 80,
            ellipsis: true,
            render: (_, { enable }) => {
              const { label, color } = localizeDiscountCodeEnable(enable);
              return (
                <Flex justify='center'>
                  <Badge count={label} color={color} />
                </Flex>
              );
            },
          },
          {
            title: 'ویژه خرید اولی‌ها',
            dataIndex: 'isOnlyForFirstTime',
            key: 'isOnlyForFirstTime',
            width: 140,
            ellipsis: true,
            render: (_, { isOnlyForFirstTime }) => (
              <Flex justify='center'>
                {isOnlyForFirstTime ? (
                  <CheckCircleOutlinedIcon className='text-green-500' />
                ) : (
                  <CloseCircleOutlinedIcon className='text-red-500' />
                )}
              </Flex>
            ),
          },
          {
            title: 'تاریخ شروع',
            dataIndex: 'startTime',
            key: 'startTime',
            width: 170,
            ellipsis: true,
            render: (_, { duration: { startTime } }) => (
              <bdi>{dateTimeFormat(new Date(startTime))}</bdi>
            ),
          },
          {
            title: 'تاریخ پایان',
            dataIndex: 'endTime',
            key: 'endTime',
            width: 170,
            ellipsis: true,
            render: (_, { duration: { endTime } }) => (
              <bdi>{dateTimeFormat(new Date(endTime))}</bdi>
            ),
          },
          {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 75,
            fixed: 'right',
            render: (_, record) => {
              const { id } = record;
              return (
                <Flex align='center' justify='center'>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'update-discount-code',
                          label: (
                            <AntdRouterLink
                              to={`${DISCOUNT_CODES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                              className='inline-block py-1 text-inherit'
                            >
                              <EditOutlinedIcon />
                              <span className='ms-2 text-sm'>
                                ویرایش کد تخفیف
                              </span>
                            </AntdRouterLink>
                          ),
                        },
                        {
                          key: 'delete-discount-code',
                          label: (
                            <React.Fragment>
                              <Button
                                danger
                                type='link'
                                size='small'
                                className='my-0.5 flex items-center px-0'
                                onClick={async () => {
                                  const confirmed =
                                    await deleteModalInstance.confirm({
                                      title:
                                        'آیا از حذف کد تخفیف مورد نظر اطمینان دارید؟',
                                      width: 500,
                                    });
                                  if (confirmed) {
                                    handleDeleteDiscountCode(record);
                                  }
                                }}
                              >
                                <DeleteOutlinedIcon />
                                <span className='text-sm'>حذف کد تخفیف</span>
                              </Button>
                              {deleteModalContextHolder}
                            </React.Fragment>
                          ),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button size='small' className='pb-[18px] pt-3.5'>
                      <MoreOutlinedIcon className='text-xl' />
                      <span className='sr-only'>آیکون اکشن‌ها</span>
                    </Button>
                  </Dropdown>
                </Flex>
              );
            },
          },
        ]}
        loading={loading}
        dataSource={discountCodesDataSource}
        pagination={{
          total,
          pageSize,
          current: pageNumber,
          showTotal: () => `در مجموع ${total} مورد`,
          onChange: (page, size) => {
            handlePageSize(size);
            if (page !== pageNumber) {
              handlePageNumber(page);
            }
          },
        }}
      />
    );
  },
);
DiscountCodesTable.displayName = 'DiscountCodesTable';

export { DiscountCodesTable };
